import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQuery } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  CircularLoadingButton,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
} from '@mint-lib/tw-components';
import { FileUploader, TextArea } from '@mint-lib/tw-components/patterns';

import {
  FILE_FORMATS,
  MAX_SIZE_BYTES,
} from '../../../constants/uploadingFiles.js';
import { useUploadDraft } from '../../../hooks/knowledge-base/useUploadDraft.js';
import { getDocumentsOption } from '../../../queries/rag.js';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const MobileUploadDrawer = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const ff = useFlags('@mint/arm', ['aiFileSystem']);

  const { data: documents = [] } = useQuery(getDocumentsOption());
  const {
    uploaderRef,
    setFiles,
    description,
    setDescription,
    validate,
    uploadFile,
    upload,
    isReady,
    isPending,
  } = useUploadDraft({
    existingFileNames: documents.map(({ filename }) => filename),
  });

  const handleUploadFile = async () => {
    const { uploaded, failed } = await upload();

    if (uploaded) {
      enqueueSnackbar({
        variant: 'success',
        subTitle: t('{{count}} file uploaded to the knowledge base', {
          count: uploaded,
        }),
        removable: true,
      });
    }

    if (!failed) onClose();
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerPortal>
        <DrawerOverlay />
        <DrawerRoot
          side="bottom"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={
            ff.aiFileSystem.enabled ? 'data-[side=bottom]:h-[90%]' : undefined
          }
        >
          <DrawerHeader onClose={onClose}>
            <DrawerTitle>{t('Upload file')}</DrawerTitle>
            <Button
              variant="contained"
              size="large"
              onClick={handleUploadFile}
              disabled={isPending || !isReady}
              className="ml-auto"
            >
              {isPending && (
                <CircularLoadingButton size="small" variant="outlined" />
              )}
              {t('Upload')}
            </Button>
          </DrawerHeader>
          <DrawerContent className="flex-col gap-4 overflow-y-auto">
            <FileUploader
              ref={uploaderRef}
              fullWidth
              accept={FILE_FORMATS}
              validate={validate}
              onChange={setFiles}
              onUpload={uploadFile}
              maxFiles={10}
              maxSize={MAX_SIZE_BYTES}
              variant="button"
            />
            <TextArea
              label={t('Description (Optional)')}
              placeholder={t('Insert a description')}
              onChange={({ target }) => setDescription(target.value)}
              value={description}
              className="min-w-full"
            />
          </DrawerContent>
        </DrawerRoot>
      </DrawerPortal>
    </Drawer>
  );
};
